import React from 'react';
import { ArrowLeft, FileText, Shield, Users, AlertTriangle } from 'lucide-react';

const TermsConditions = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={onBack}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </button>
          
          <div className="bg-white rounded-3xl p-8 shadow-lg">
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900">Terms & Conditions</h1>
                <p className="text-gray-600 mt-2">Effective Date: January 1, 2025</p>
              </div>
            </div>
            
            <p className="text-lg text-gray-700 leading-relaxed">
              By using Shortlistly, you agree to the following terms and conditions. Please read them carefully.
            </p>
          </div>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          {/* Use of Service */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center mb-6">
              <Users className="w-6 h-6 text-blue-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Use of Service</h2>
            </div>
            <div className="space-y-6">
              <div className="border-l-4 border-blue-500 pl-6">
                <h3 className="font-semibold text-gray-900 mb-3">Eligibility Requirements</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>You must be at least 16 years old to use this service</li>
                  <li>You must provide accurate and complete information</li>
                  <li>You are responsible for maintaining account security</li>
                </ul>
              </div>
              
              <div className="border-l-4 border-red-500 pl-6">
                <h3 className="font-semibold text-gray-900 mb-3">Prohibited Activities</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Misusing or attempting to reverse-engineer the platform</li>
                  <li>Uploading malicious content or illegal materials</li>
                  <li>Sharing account credentials with unauthorized users</li>
                  <li>Using the service for any unlawful purposes</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Intellectual Property */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center mb-6">
              <Shield className="w-6 h-6 text-green-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">Intellectual Property</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 text-lg">Our Rights</h3>
                <div className="bg-blue-50 p-4 rounded-xl">
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Shortlistly software and platform</li>
                    <li>AI models and algorithms</li>
                    <li>Branding, logos, and trademarks</li>
                    <li>Resume templates and designs</li>
                  </ul>
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 text-lg">Your Rights</h3>
                <div className="bg-green-50 p-4 rounded-xl">
                  <ul className="list-disc list-inside space-y-2 text-gray-700">
                    <li>Your original resume content</li>
                    <li>Personal information and data</li>
                    <li>Generated resume variations</li>
                    <li>Account preferences and settings</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* AI Content Disclaimer */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center mb-6">
              <AlertTriangle className="w-6 h-6 text-orange-600 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900">AI Content Disclaimer</h2>
            </div>
            <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-xl">
              <h3 className="font-semibold text-gray-900 mb-3">Important Notice</h3>
              <p className="text-gray-700 mb-4">
                AI-generated resume suggestions and optimizations are meant for assistance and guidance only. 
                They should not be considered as professional career advice.
              </p>
              <div className="space-y-2 text-gray-700">
                <p><strong>Your Responsibility:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Always review and verify all AI-generated content</li>
                  <li>Ensure accuracy of personal information and achievements</li>
                  <li>Finalize and proofread before submitting to employers</li>
                  <li>Adapt suggestions to your specific situation and industry</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Service Availability */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Service Availability & Limitations</h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl">
                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">1</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Service Uptime</h3>
                  <p className="text-gray-600">We strive for 99.9% uptime but cannot guarantee uninterrupted service</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl">
                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">2</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Maintenance Windows</h3>
                  <p className="text-gray-600">Scheduled maintenance may temporarily interrupt service</p>
                </div>
              </div>
              
              <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl">
                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-white font-bold text-sm">3</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Feature Updates</h3>
                  <p className="text-gray-600">We may modify or discontinue features with advance notice</p>
                </div>
              </div>
            </div>
          </div>

          {/* Changes to Terms */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Changes to Terms</h2>
            <div className="bg-blue-50 p-6 rounded-xl">
              <p className="text-gray-700 mb-4">
                We reserve the right to update these terms and conditions at any time. When we do:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li>Users will be notified via email to their registered address</li>
                <li>Changes will be announced on the platform</li>
                <li>Continued use constitutes acceptance of new terms</li>
                <li>Users may close their account if they disagree with changes</li>
              </ul>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
            <h2 className="text-2xl font-bold mb-4">Questions About Terms?</h2>
            <p className="text-blue-100 mb-6">
              If you have any questions about these terms and conditions, please don't hesitate to contact us.
            </p>
            <a 
              href="mailto:shortlistly@gmail.com"
              className="inline-flex items-center px-6 py-3 bg-white text-blue-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;